import { useEffect, useState } from "react";
import type { TranslateFn } from "../../i18n";

const PER_SLIDE = 2;
const CYCLE_MS = 2500;

export type ProductPhotoCarouselItem = {
  image: string;
  title?: string;
  href?: string;
};

/**
 * Photo strip — shows PER_SLIDE images per row with even spacing, advances
 * to the next group every CYCLE_MS, dot indicators below. Lives at the
 * bottom of the single-garment right panel to entertain users while the
 * try-on is generating.
 */
export function ProductPhotoCarouselCard({
  photos,
  items,
  productTitle,
  t,
}: {
  photos?: string[];
  items?: ProductPhotoCarouselItem[];
  productTitle?: string;
  t: TranslateFn;
}) {
  const completeLookItems = items?.filter((item) => !!item.image) ?? [];
  const isCompleteLook = completeLookItems.length > 0;
  const photoItems: ProductPhotoCarouselItem[] = (photos ?? []).filter(Boolean).map((src) => ({
      image: src,
      ...(productTitle ? { title: productTitle } : {}),
  }));
  const seen = new Set<string>();
  const entries: ProductPhotoCarouselItem[] = [];
  const pushEntry = (item: ProductPhotoCarouselItem) => {
    if (!item.image || seen.has(item.image)) return;
    seen.add(item.image);
    entries.push(item);
  };
  completeLookItems.forEach(pushEntry);
  photoItems.forEach(pushEntry);
  const [groupIdx, setGroupIdx] = useState(0);
  const totalGroups = Math.max(1, Math.ceil(entries.length / PER_SLIDE));

  useEffect(() => {
    if (totalGroups < 2) return;
    const id = setInterval(() => setGroupIdx((v) => (v + 1) % totalGroups), CYCLE_MS);
    return () => clearInterval(id);
  }, [totalGroups]);

  useEffect(() => {
    setGroupIdx((v) => Math.min(v, totalGroups - 1));
  }, [totalGroups]);

  if (entries.length === 0) return null;

  const start = groupIdx * PER_SLIDE;
  const slide = entries.slice(start, start + PER_SLIDE);

  return (
    <div className="ps-tryon-photo-strip" role="group" aria-label={t("Product photos")}>
      {isCompleteLook && (
        <div className="ps-tryon-photo-strip-head">
          <span className="ps-tryon-photo-strip-badge">{t("Complete the look")}</span>
        </div>
      )}
      <div className={`ps-tryon-photo-strip-row ps-count-${slide.length}`} key={groupIdx}>
        {slide.map((item, i) => {
          const content = <img src={item.image} alt={item.title || productTitle || ""} draggable={false} />;
          return item.href ? (
            <a
              key={`${groupIdx}-${i}`}
              className="ps-tryon-photo-strip-cell"
              href={item.href}
              target={isCompleteLook ? "_blank" : undefined}
              rel={isCompleteLook ? "noopener noreferrer" : undefined}
              aria-label={item.title || productTitle || t("Product photos")}
            >
              {content}
            </a>
          ) : (
            <div key={`${groupIdx}-${i}`} className="ps-tryon-photo-strip-cell">
              {content}
            </div>
          );
        })}
      </div>
      {totalGroups > 1 && (
        <div className="ps-tryon-photo-strip-dots" aria-hidden="true">
          {Array.from({ length: totalGroups }).map((_, i) => (
            <span key={i} className={`ps-tryon-photo-strip-dot${i === groupIdx ? " is-active" : ""}`} />
          ))}
        </div>
      )}
    </div>
  );
}
